import type { SerializedRun } from '@prism/sim';

/**
 * Local save data (TRD-10 §2).
 *
 * One key, one versioned blob, a migration chain from day one. The alternative — adding
 * `version` when the first breaking change arrives — means that change wipes every
 * existing player's unlock tree, and there is no way to recover it.
 */

export const SAVE_KEY = 'prism.save.v1';
export const CORRUPT_KEY = 'prism.save.v1.corrupt';
export const PENDING_KEY = 'prism.pendingSubmissions';

export const CURRENT_SAVE_VERSION = 1;

export interface Settings {
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  screenshakeScale: number;
  damageNumbers: 'all' | 'crits-only' | 'off';
  showDamageFlash: boolean;
  hitstopEnabled: boolean;
  colorblindMode: boolean;
  /** Fast-forward the stretches where nothing can be shot — see `idleSkip.ts`. */
  autoFastForward: boolean;
  /** The synergy panel starts open; folding it is remembered across runs. */
  synergyPanelOpen: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  // 0.6, not 1. Browser games that open at full volume get closed (TRD-04 R-04-30).
  masterVolume: 0.6,
  sfxVolume: 1,
  musicVolume: 0.7,
  screenshakeScale: 1,
  damageNumbers: 'all',
  showDamageFlash: true,
  hitstopEnabled: true,
  colorblindMode: false,
  autoFastForward: true,
  synergyPanelOpen: true,
};

export interface DailyHistoryEntry {
  date: string;
  score: number;
  wavesCleared: number;
  submitted: boolean;
}

export interface SaveData {
  version: number;
  /** Anonymous. No accounts, no email, no PII of any kind (R-10-05, GDD §11). */
  playerId: string;
  displayName: string;
  shardsEarned: number;
  shardsSpent: number;
  ownedNodes: string[];
  stats: {
    runsPlayed: number;
    bestWave: Record<string, number>;
    winsPerMap: Record<string, number>;
    totalKills: number;
  };
  settings: Settings;
  dailyHistory: DailyHistoryEntry[];
  activeRun: SerializedRun | null;
  /**
   * The last UTC date the feedback form was offered — submitted OR dismissed.
   *
   * The prompt is once a day, not once a run. By the fourth prompt in a session people
   * stop reading it, and bored answers are worse than no answers: they look like data.
   * Empty string means never asked.
   */
  lastFeedbackDate: string;
  /**
   * The Discovery log (playtest request): what you have fielded and what you have faced.
   *
   * Permanent across runs, which is the whole point — a compendium that reset every run
   * would be a scoreboard, not a record. `towers` is every defId ever placed; `enemyKills`
   * is keyed by archetype and its PRESENCE means "met", so an enemy that has only ever
   * walked past you still gets a page, at zero kills.
   */
  discovery: {
    towers: string[];
    enemyKills: Record<string, number>;
  };
}

export function createSave(playerId: string): SaveData {
  return {
    version: CURRENT_SAVE_VERSION,
    playerId,
    displayName: '',
    shardsEarned: 0,
    shardsSpent: 0,
    ownedNodes: [],
    stats: { runsPlayed: 0, bestWave: {}, winsPerMap: {}, totalKills: 0 },
    settings: { ...DEFAULT_SETTINGS },
    dailyHistory: [],
    activeRun: null,
    lastFeedbackDate: '',
    discovery: { towers: [], enemyKills: {} },
  };
}

/**
 * Minimal storage surface, so the whole module is testable without a browser and a
 * quota-exceeded failure can be exercised deliberately.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class MemoryStorage implements StorageLike {
  private readonly map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

// ── Migration (R-10-02) ───────────────────────────────────────────────────────────────

type Migration = (save: Record<string, unknown>) => Record<string, unknown>;

/**
 * Indexed by the version being migrated FROM.
 *
 * Empty today because v1 is the first schema. It exists anyway: the cost of adding the
 * chain now is nothing, and the cost of adding it after players have saves is their data.
 */
const MIGRATIONS: Record<number, Migration> = {};

export function migrate(raw: Record<string, unknown>): SaveData {
  let save = raw;
  let guard = 0;

  while (typeof save['version'] === 'number' && save['version'] < CURRENT_SAVE_VERSION) {
    const from = save['version'];
    const step = MIGRATIONS[from];
    if (!step) break;
    save = step(save);
    if (++guard > 32) break;
  }

  save['version'] = CURRENT_SAVE_VERSION;
  return save as unknown as SaveData;
}

// ── Load / save ───────────────────────────────────────────────────────────────────────

export interface LoadResult {
  save: SaveData;
  /** True when the stored blob was unreadable and a fresh save was substituted. */
  recovered: boolean;
}

/**
 * Read the save, or start fresh.
 *
 * Every path is guarded. Corrupt `localStorage` must never produce a white screen — and
 * the bad blob is QUARANTINED rather than deleted, because a player reporting "I lost my
 * unlocks" is someone whose data might still be recoverable by hand (R-10-04).
 */
export function loadSave(storage: StorageLike, newId: () => string): LoadResult {
  let raw: string | null = null;
  try {
    raw = storage.getItem(SAVE_KEY);
  } catch {
    return { save: createSave(newId()), recovered: true };
  }

  if (raw === null) return { save: createSave(newId()), recovered: false };

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('save is not an object');
    }
    const migrated = migrate(parsed as Record<string, unknown>);
    if (typeof migrated.playerId !== 'string' || migrated.playerId.length === 0) {
      migrated.playerId = newId();
    }
    migrated.settings = { ...DEFAULT_SETTINGS, ...(migrated.settings ?? {}) };
    // Added after v1 shipped. `migrate` casts rather than fills, so a save written before
    // this field existed would carry `undefined` behind a `string` type — harmless here
    // (it just means "never asked") but the kind of lie that bites the next reader.
    migrated.lastFeedbackDate ??= '';
    // Same story as the field above: `migrate` casts rather than fills, so a save written
    // before Discovery existed would carry `undefined` behind a typed object.
    migrated.discovery ??= { towers: [], enemyKills: {} };
    migrated.discovery.towers ??= [];
    migrated.discovery.enemyKills ??= {};
    return { save: migrated, recovered: false };
  } catch {
    try {
      storage.setItem(CORRUPT_KEY, raw);
    } catch {
      /* quarantine is best-effort; never let it mask the recovery */
    }
    return { save: createSave(newId()), recovered: true };
  }
}

export function writeSave(storage: StorageLike, save: SaveData): boolean {
  try {
    storage.setItem(SAVE_KEY, JSON.stringify(save));
    return true;
  } catch {
    // Quota exceeded, or storage disabled entirely (private browsing). The game keeps
    // running in memory rather than dying on a write.
    return false;
  }
}

/**
 * Debounced writer with a flush.
 *
 * `localStorage` is synchronous, so writing on every state change stutters the frame
 * (R-10-03). Flushing on `visibilitychange` and `beforeunload` is what makes the debounce
 * safe — otherwise closing the tab within the debounce window loses the run.
 */
export class SaveWriter {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: SaveData | null = null;

  constructor(
    private readonly storage: StorageLike,
    private readonly delayMs = 500,
  ) {}

  schedule(save: SaveData): void {
    this.pending = save;
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, this.delayMs);
  }

  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.pending) return;
    writeSave(this.storage, this.pending);
    this.pending = null;
  }

  get hasPending(): boolean {
    return this.pending !== null;
  }
}

/** `crypto.randomUUID` where available, with a deterministic-shape fallback. */
export function newPlayerId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Older Safari and non-secure contexts. Shape matters — the server validates a UUID.
  const hex = (n: number): string =>
    Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}
